import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { Reveal } from '../components/Motion';
import { REAL_CATEGORIES } from '../data/site';

// Ultra-premium, minimal — 5 large category cards inspired by Apple / Porsche / Framer / Linear.
export default function Realisations() {
  return (
    <>
      {/* Header */}
      <section className="relative pt-16 md:pt-24 pb-10 md:pb-16">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10">
          <Reveal>
            <div className="overline mb-6">◍ Réalisations</div>
            <h1 className="font-display italic text-[13vw] sm:text-[10vw] lg:text-[7.5vw] leading-[0.9] tracking-tight max-w-5xl">
              Réalisations.
            </h1>
            <p className="mt-8 max-w-xl text-white/60 text-base md:text-lg leading-relaxed">
              Une sélection sobre de nos savoir-faire. Choisissez une catégorie
              pour découvrir un projet emblématique.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Grid of 5 category cards — compact, editorial, generous whitespace */}
      <section className="relative pb-24 md:pb-32">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-60px' }}
            variants={{
              hidden: {},
              show: { transition: { staggerChildren: 0.09, delayChildren: 0.1 } },
            }}
            className="grid grid-cols-12 gap-6 md:gap-8 lg:gap-10"
          >
            {REAL_CATEGORIES.filter((c) => !c.hideDetail).map((c, i) => {
              // Row 1: 2 wide cards (7 / 5). Row 2: 3 cards (4 / 4 / 4).
              const isBig = i < 2;
              const colClass = isBig
                ? (i === 0 ? 'col-span-12 md:col-span-7' : 'col-span-12 md:col-span-5')
                : 'col-span-12 sm:col-span-6 md:col-span-4';
              return (
                <motion.div
                  key={c.slug}
                  variants={{
                    hidden: { opacity: 0, y: 32 },
                    show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.2, 0.8, 0.2, 1] } },
                  }}
                  className={colClass}
                >
                  <Link
                    to={`/realisations/${c.slug}`}
                    data-testid={`category-card-${c.slug}`}
                    className="hero-media group relative block w-full h-[300px] md:h-[420px] lg:h-[500px] overflow-hidden rounded-2xl bg-black border border-white/10 hover:border-white/25 transition-colors"
                    style={{ boxShadow: 'var(--shadow-elev)' }}
                  >
                    <img
                      src={c.image}
                      alt={c.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.045] transition-[transform,opacity] duration-[1100ms] ease-out"
                      style={{ filter: 'grayscale(15%) contrast(1.03)' }}
                    />
                    {/* Subtle bottom gradient — only for legibility */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />

                    {/* Chrome */}
                    <div className="absolute inset-0 p-6 md:p-7 lg:p-8 flex flex-col justify-between">
                      <div className="flex items-start justify-between">
                        <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/70">
                          {String(i + 1).padStart(2, '0')} / Catégorie
                        </span>
                        <span className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-white/25 backdrop-blur-md bg-white/5 text-white opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-500">
                          <ArrowUpRight size={15} strokeWidth={1.5} />
                        </span>
                      </div>
                      <div>
                        <h3 className="font-display italic text-3xl md:text-4xl lg:text-[42px] leading-[0.98] tracking-tight text-white">
                          {c.title}
                        </h3>
                        <p className="mt-2 text-[13px] md:text-sm text-white/75 leading-snug line-clamp-1 max-w-lg">
                          {c.subtitle}
                        </p>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>
    </>
  );
}
